import { IconMinus, IconPlus } from "@tabler/icons-react";
import { useWatch } from "react-hook-form";
import { useLanguage } from "../../../context/LanguageContext";

import SelectInput from "../../../components/Select";
import Input from "../../../components/Input";

// Define Product Option type (based on what useProductAllListData returns)
type ProductOption = {
    id: string;
    itemName: string;
    weight: number;
};

interface ShipmentItemRowProps {
    index: number;
    control: any;
    setValue: any;
    register: any;
    errors: any;
    products: ProductOption[] | undefined; // List of all available products
    onRemove: () => void;
    onAdd: () => void;
    isLastRow: boolean;
    lastRowQuantity: string | undefined; // Prop passed from parent to manage Add button state
    isSidebar?: boolean;
}

export default function ShipmentItemRow({
    index,
    control,
    setValue,
    register,
    errors,
    products,
    onRemove,
    onAdd,
    isLastRow,
    lastRowQuantity,
    isSidebar = false,
}: ShipmentItemRowProps) {
    const { t } = useLanguage();
    const pId = useWatch({
        control,
        name: `products[${index}].pId`,
    }) as string | undefined;

    const weight = useWatch({
        control,
        name: `products[${index}].weight`,
    }) as number | undefined;

    const selectedProduct = products?.find((p) => p.id === pId);

    const isAddButtonDisabled = !pId || !lastRowQuantity;

    if (isSidebar) {
        return (
            <div className="flex flex-col gap-2.5 p-3 bg-slate-50/60 rounded-2xl border border-slate-100 relative w-full col-span-2">
                {/* Row 1: Product dropdown and add/remove button */}
                <div className="flex items-end gap-2 w-full">
                    <div className="flex-1 min-w-0">
                        <SelectInput
                            name={`products[${index}].pId`}
                            value={pId ?? ""}
                            label={t("Select Product")}
                            placeholder={t("Select Product")}
                            selectOptions={
                                products?.map((p) => ({
                                    id: p.id,
                                    value: p.id,
                                    text: p.itemName,
                                })) ?? []
                            }
                            onChange={(val) => {
                                setValue(`products[${index}].pId`, val, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                });
                                const selected = products?.find((p) => p.id === val);
                                if (selected) {
                                    setValue(`products[${index}].weight`, selected.weight, {
                                        shouldValidate: true,
                                    });
                                }
                            }}
                            error={errors?.products?.[index]?.pId}
                            validation={{ required: "Please select a product" }}
                            className="w-full"
                        />
                    </div>
                    {/* Add / Remove Button */}
                    {isLastRow ? (
                        <button
                            type="button"
                            disabled={isAddButtonDisabled}
                            onClick={onAdd}
                            className={`flex-shrink-0 w-[38px] h-[38px] rounded-xl flex items-center justify-center bg-yellow-100 text-black border border-yellow-400 transition hover:bg-yellow-200 ${!isAddButtonDisabled ? "" : "opacity-70 cursor-not-allowed"}`}
                        >
                            <IconPlus size={18} />
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={onRemove}
                            className="flex-shrink-0 w-[38px] h-[38px] rounded-xl flex items-center justify-center bg-red-100 text-red-600 border border-red-400 transition hover:bg-red-200"
                        >
                            <IconMinus size={18} />
                        </button>
                    )}
                </div>

                {/* Row 2: Qty and Weight side-by-side */}
                <div className="flex items-end gap-3 w-full">
                    {/* Quantity */}
                    <div className="flex-1">
                        <Input
                            name={`products[${index}].quantity`}
                            disabled={!pId}
                            type="number"
                            title={t("Qty")}
                            register={register}
                            error={errors?.products?.[index]?.quantity}
                            validation={{
                                required: "Quantity is required",
                                min: { value: 1, message: "Qty ≥ 1" },
                                validate: (v: any) => {
                                    const n = Number(v);
                                    return (!isNaN(n) && n > 0) || "Invalid qty";
                                },
                            }}
                            className="w-full"
                        />
                    </div>

                    {/* Weight display */}
                    <div className="flex-1 text-sm text-gray-700">
                        <div className="text-xs text-gray-500 mb-1">{t("Weight (kg)")}</div>
                        <div className="border border-gray-300 rounded-xl px-3 py-1.5 bg-gray-50 text-center font-medium text-xs leading-[18px] flex items-center justify-center h-[34px]">
                            {weight ?? selectedProduct?.weight ?? 0} {t("kg")}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-end gap-2 col-span-2">
            {/* Product Selector */}
            <SelectInput
                name={`products[${index}].pId`} // optional – only for label/error
                value={pId ?? ""}
                label={t("Select Product")}
                placeholder={t("Select Product")}
                selectOptions={
                    products?.map((p) => ({
                        id: p.id,
                        value: p.id,
                        text: p.itemName,
                    })) ?? []
                }
                onChange={(val) => {
                    // Update the product ID
                    setValue(`products[${index}].pId`, val, {
                        shouldValidate: true,   // ← This triggers validation and clears errors
                        shouldDirty: true,
                    });

                    // Optional: also update weight automatically when product changes
                    const selected = products?.find((p) => p.id === val);
                    if (selected) {
                        setValue(`products[${index}].weight`, selected.weight, {
                            shouldValidate: true,
                        });
                    }
                }}
                // Do NOT pass register here!
                error={errors?.products?.[index]?.pId}
                validation={{ required: "Please select a product" }}
                className="w-full"
            />

            {/* Quantity */}
            <Input
                name={`products[${index}].quantity`}
                disabled={!pId}
                type="number"
                title={t("Qty")}
                register={register} // ← This is fine for native <input>
                error={errors?.products?.[index]?.quantity}
                validation={{
                    required: "Quantity is required",
                    min: { value: 1, message: "Qty ≥ 1" },
                    validate: (v: any) => {
                        const n = Number(v);
                        return (!isNaN(n) && n > 0) || "Invalid quantity";
                    },
                }}
                className="w-28"
            />

            {/* Weight display */}
            <div className="w-28 text-sm text-gray-700">
                <div className="text-xs text-gray-500">{t("Weight (kg)")}</div>
                <div className="border border-gray-300 rounded-full px-3 py-2 bg-gray-50 text-center">
                    {weight ?? selectedProduct?.weight ?? 0}
                </div>
            </div>

            {/* Add / Remove Button */}
            {isLastRow ? (
                <button
                    type="button"
                    disabled={isAddButtonDisabled}
                    onClick={onAdd}
                    className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-yellow-100 text-black border border-yellow-400 transition hover:bg-yellow-200 ${!isAddButtonDisabled ? "" : "opacity-70 cursor-not-allowed"
                        }`}
                >
                    <IconPlus size={20} />
                </button>
            ) : (
                <button
                    type="button"
                    onClick={onRemove}
                    className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-red-100 text-red-600 border border-red-400 transition hover:bg-red-200"
                >
                    <IconMinus size={20} />
                </button>
            )}
        </div>
    );
}